import { SeoHead } from '../components/SeoHead';
import React, { useState } from 'react';
import { CheckCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useForcedDarkMode } from '../contexts/ThemeContext';

const QUESTIONS = [
  {
    id: 'headache',
    question: "What's your biggest compliance headache right now?",
    options: [
      { value: 'pre_audit', label: 'Pre-audit scramble', icon: '🔥' },
      { value: 'questionnaires', label: 'Client questionnaires', icon: '📋' },
      { value: 'cost', label: 'Cost of tooling', icon: '💰' },
      { value: 'no_time', label: 'No time', icon: '⏰' },
      { value: 'business_need', label: 'A wider business need: contracts, projects, company secretarial or M&A', icon: '🧭' },
      { value: 'other', label: 'Something else', icon: '💭' }
    ]
  },
  {
    id: 'frameworks',
    question: 'What frameworks are you working to?',
    options: [
      { value: 'iso27001', label: 'ISO 27001', icon: '🛡️' },
      { value: 'uk_gdpr', label: 'UK GDPR', icon: '🇬🇧' },
      { value: 'cyber_essentials', label: 'Cyber Essentials', icon: '🔐' },
      { value: 'soc2', label: 'SOC 2', icon: '📊' },
      { value: 'other', label: 'Other', icon: '📑' },
      { value: 'none', label: 'None yet', icon: '🆕' }
    ],
    multiSelect: true
  },
  {
    id: 'current_setup',
    question: 'What does your current compliance setup look like?',
    options: [
      { value: 'spreadsheets', label: 'Spreadsheets', icon: '📊' },
      { value: 'platform', label: 'Existing platform', icon: '💻' },
      { value: 'consultant', label: 'Consultant', icon: '👔' },
      { value: 'nothing', label: 'Nothing formal', icon: '🤷' }
    ]
  },
  {
    id: 'contact',
    question: 'Want us to reach out with tailored recommendations?',
    type: 'contact',
    optional: true
  }
];

const EnterpriseQuestionnairePage = () => {
  useForcedDarkMode();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [selectedMulti, setSelectedMulti] = useState([]);
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');

  const question = QUESTIONS[currentQuestion];
  const isLastQuestion = currentQuestion === QUESTIONS.length - 1;
  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  const handleSelect = (value) => {
    if (question.multiSelect) {
      setSelectedMulti(prev => 
        prev.includes(value) 
          ? prev.filter(v => v !== value)
          : [...prev, value]
      );
    } else {
      // Single select - auto advance
      const newAnswers = { ...answers, [question.id]: value };
      setAnswers(newAnswers);
      
      if (isLastQuestion) {
        handleSubmit(newAnswers);
      } else {
        setTimeout(() => setCurrentQuestion(prev => prev + 1), 300);
      }
    }
  };

  const handleMultiContinue = () => {
    if (selectedMulti.length > 0) {
      const newAnswers = { ...answers, [question.id]: selectedMulti };
      setAnswers(newAnswers);
      setSelectedMulti([]);
      
      if (isLastQuestion) {
        handleSubmit(newAnswers);
      } else {
        setCurrentQuestion(prev => prev + 1);
      }
    }
  };

  const handleSubmit = async (finalAnswers) => {
    // Store response with optional contact info
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/questionnaire/enterprise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: finalAnswers,
          contact_email: contactEmail || null,
          contact_name: contactName || null,
          submitted_at: new Date().toISOString(),
          source: 'enterprise_questionnaire'
        })
      });
    } catch (e) {
      // Silent fail - we show success anyway
    }
    setSubmitted(true);
  };

  const handleContactSubmit = () => {
    const finalAnswers = { ...answers };
    if (contactEmail) {
      finalAnswers.contact_email = contactEmail;
      finalAnswers.contact_name = contactName;
    }
    handleSubmit(finalAnswers);
  };

  const handleSkipContact = () => {
    handleSubmit(answers);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <SeoHead path="/questionnaire/enterprise" />
        <div className="max-w-md w-full text-center">
          <div className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary/30 to-emerald-500/30 backdrop-blur-sm">
            <CheckCircle className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Thank you!
          </h1>
          <p className="text-slate-400 mb-8">
            Your responses help us tailor your Simplif-i experience. Our enterprise team will be in touch shortly.
          </p>
          <div className="p-6 rounded-2xl bg-slate-800/50 backdrop-blur border border-slate-700/50">
            <p className="text-sm text-slate-500 mb-2">In the meantime</p>
            <a 
              href="/public" 
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Explore our platform <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      <SeoHead path="/questionnaire/enterprise" />
      {/* Header */}
      <header className="p-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Link to="/" aria-label="Back to the Simplif-i site" data-testid="questionnaire-home-link"><Logo className="h-10" showText={false} /></Link>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span className="hidden sm:flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Short questionnaire</span>
            <Link to="/contact" className="text-[#4fb3a2] hover:underline" data-testid="questionnaire-contact-link">Not about compliance? Contact us</Link>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="px-6">
        <div className="max-w-2xl mx-auto">
          <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-emerald-400 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-slate-600">
            <span>Question {currentQuestion + 1} of {QUESTIONS.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
        </div>
      </div>

      {/* Question */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <h1 data-testid="questionnaire-question-heading" className="text-2xl md:text-3xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {question.question}
          </h1>

          {/* Contact Form Question */}
          {question.type === 'contact' ? (
            <div className="space-y-6">
              <div className="p-6 rounded-2xl bg-slate-800/50 backdrop-blur border border-slate-700/50">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Your name</label>
                    <input
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">Work email</label>
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="john@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleContactSubmit}
                  disabled={!contactEmail}
                  className={`
                    px-8 py-3 rounded-xl font-medium transition-all duration-200
                    ${contactEmail
                      ? 'bg-primary text-slate-900 hover:bg-primary/90 shadow-lg shadow-primary/30'
                      : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    }
                  `}
                >
                  Get Recommendations <ChevronRight className="w-4 h-4 inline ml-1" />
                </button>
                <button
                  onClick={handleSkipContact}
                  className="px-8 py-3 rounded-xl font-medium text-slate-400 hover:text-white transition-colors"
                >
                  Skip for now
                </button>
              </div>
              
              <p className="text-center text-slate-500 text-sm">
                We'll send you personalized recommendations based on your answers
              </p>
            </div>
          ) : (
            /* Regular Options */
            <>
              <div className="grid gap-3">
                {question.options.map((option) => {
                  const isSelected = question.multiSelect 
                    ? selectedMulti.includes(option.value)
                    : answers[question.id] === option.value;

                  return (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(option.value)}
                      className={`
                        group relative p-4 rounded-xl border-2 transition-all duration-200
                        ${isSelected 
                          ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20' 
                          : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600 hover:bg-slate-800/50'
                        }
                      `}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{option.icon}</span>
                        <span className={`text-lg font-medium ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {option.label}
                        </span>
                        {isSelected && (
                          <CheckCircle className="w-5 h-5 text-primary ml-auto" />
                        )}
                      </div>
                      {!question.multiSelect && (
                        <div className={`
                          absolute inset-0 rounded-xl border-2 border-primary opacity-0 
                          group-hover:opacity-20 transition-opacity pointer-events-none
                        `} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Multi-select continue button */}
              {question.multiSelect && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleMultiContinue}
                    disabled={selectedMulti.length === 0}
                    className={`
                      px-8 py-3 rounded-xl font-medium transition-all duration-200
                      ${selectedMulti.length > 0
                        ? 'bg-primary text-slate-900 hover:bg-primary/90 shadow-lg shadow-primary/30'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                      }
                    `}
                  >
                    Continue <ChevronRight className="w-4 h-4 inline ml-1" />
                  </button>
                </div>
              )}

              {/* Hint */}
              {question.multiSelect && (
                <p className="text-center text-slate-500 text-sm mt-4">
                  Select all that apply
                </p>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center">
        <p className="text-xs text-slate-600">
          {question.type === 'contact' 
            ? 'Your info is secure • We only use it to send relevant recommendations'
            : 'Quick questionnaire • Helps us understand your needs'
          }
        </p>
      </footer>
    </div>
  );
};

export default EnterpriseQuestionnairePage;
